import { isPaymentAccountKey } from '@metycle/shared';
import type { DefaultValues } from 'react-hook-form';

import { deriveOriginalDueDate } from '../lib/payment-terms';
import { parseIsoDate, todayIso } from './trade-invoice-dates';
import { referencePriceFromTicket } from '../lib/pricing-basis';
import type { InvoiceRow } from '../types';
import type { ProvisionalSubmitValues } from './provisional-invoice-schema';
import type { FinalSettlementSubmitValues } from './final-settlement-invoice-schema';
import { applySellFinalCreateDefaults } from './final-settlement-sell-payload';
import { applyItemAndTerm } from './prefill-item-term';

export interface TicketContextForTradeInvoice {
  readonly currency: 'USD' | 'EUR' | null;
  readonly counterpartyName: string | null;
  readonly paymentTerms: string | null;
  readonly pricing_type: string | null;
  readonly signed_price: string | null;
  readonly lme_price: string | null;
  readonly premium_discount: string | null;
  readonly payable_percent: string | null;
}

export interface BlContextForTradeInvoice {
  readonly shippedQuantityMt: string | null;
  // Human-facing identifiers surfaced in the sell-side Final Invoice header
  // (Launchpad parity). Null-safe: the header renders an em dash when absent.
  readonly contractNumber: string | null;
  readonly blOrderName: string | null;
  readonly blNumber: string | null;
}

export { isoDateValue } from './trade-invoice-dates';

interface BaseDefaultsArgs {
  readonly side: 'buy' | 'sell';
  readonly ticket: TicketContextForTradeInvoice;
  readonly bl: BlContextForTradeInvoice;
  readonly allocatedDownpayment: number;
  // Order-side open advance when the tracking-read fields are present, else null
  // (falls back to the shipment-ratio `allocatedDownpayment`).
  readonly advanceOpen: number | null;
}

// VAT is deliberately absent here so a fresh invoice leaves the field blank
// (the "leave blank if not on invoice" placeholder shows). The edit path fills
// it from the saved invoice, and the schema settles a blank field to 0.
type TradeDefaultsBase = Omit<ProvisionalSubmitValues, 'vat_amount'>;

function baseDefaults(args: BaseDefaultsArgs): TradeDefaultsBase {
  const refPrice = referencePriceFromTicket(args.ticket) ?? 0;
  const qty = args.bl.shippedQuantityMt === null ? 0 : Number(args.bl.shippedQuantityMt);
  const gross = refPrice * qty;
  // Suggest the side's open advance capped at gross (so the pre-fill never trips
  // the issue-time over-deduction guard); the legacy shipment-ratio figure when
  // no open advance is served.
  const downpayment =
    args.advanceOpen === null
      ? args.allocatedDownpayment
      : Math.max(Math.min(args.advanceOpen, gross), 0);
  const total = Math.max(gross - downpayment, 0);
  const dueIso = deriveOriginalDueDate({ downpaymentDueDate: null });
  const result: TradeDefaultsBase = {
    invoice_number: '',
    currency: args.ticket.currency ?? 'USD',
    total_amount: total,
    status: 'Draft',
    // Pre-fill the supplier invoice date with today (Launchpad parity).
    issue_date: todayIso(),
    original_due_date: parseIsoDate(dueIso),
    market_reference_price: refPrice,
    market_reference_date: parseIsoDate(todayIso()),
    amount_qt_mt: qty,
    applied_downpayment_amount: downpayment,
    payment_account_key: 'auto',
  };
  if (args.ticket.counterpartyName !== null) {
    result.company_name = args.ticket.counterpartyName;
    if (args.side === 'buy') {
      result.issuer_company_name = args.ticket.counterpartyName;
    }
  }
  return result;
}

function fallbackNumber(raw: string | null, fallback: number): number {
  return raw === null ? fallback : Number(raw);
}

function fallbackDate(raw: string | null, fallback: Date): Date {
  return raw === null ? fallback : parseIsoDate(raw.slice(0, 10));
}

function withExisting(
  base: TradeDefaultsBase,
  existing: InvoiceRow,
  side: 'buy' | 'sell',
): ProvisionalSubmitValues {
  const result: ProvisionalSubmitValues = {
    ...base,
    invoice_number: existing.invoice_number ?? '',
    currency: (existing.currency ?? base.currency) as 'USD' | 'EUR',
    total_amount: fallbackNumber(existing.total_amount, base.total_amount),
    vat_amount: fallbackNumber(existing.vat_amount, 0),
    original_due_date: fallbackDate(existing.original_due_date, base.original_due_date),
    issue_date: existing.issue_date === null ? undefined : existing.issue_date.slice(0, 10),
    market_reference_price: fallbackNumber(
      existing.market_reference_price,
      base.market_reference_price,
    ),
    market_reference_date: fallbackDate(existing.market_reference_date, base.market_reference_date),
    amount_qt_mt: fallbackNumber(existing.amount_qt_mt, base.amount_qt_mt),
    applied_downpayment_amount: fallbackNumber(
      existing.applied_downpayment_amount,
      base.applied_downpayment_amount,
    ),
    payment_account_key:
      existing.payment_account_key !== null && isPaymentAccountKey(existing.payment_account_key)
        ? existing.payment_account_key
        : 'auto',
  };
  if (existing.company_name !== null) result.company_name = existing.company_name;
  if (side === 'buy' && existing.issuer_company_name !== null) {
    result.issuer_company_name = existing.issuer_company_name;
  }
  applyItemAndTerm(result, existing);
  return result;
}

export interface BuildProvisionalDefaultsArgs extends BaseDefaultsArgs {
  readonly existing: InvoiceRow | null;
}

export function buildProvisionalDefaults(
  args: BuildProvisionalDefaultsArgs,
): DefaultValues<ProvisionalSubmitValues> {
  const base = baseDefaults(args);
  if (args.existing === null) return base;
  return {
    ...withExisting(base, args.existing, args.side),
    reclassify_to_invoice_type: 'Provisional',
  };
}

export interface BuildFinalSettlementDefaultsArgs extends BaseDefaultsArgs {
  readonly existing: InvoiceRow | null;
  readonly appliedProvisional: number;
  // The chain-aware server suggestion for the sell side's hidden number field
  // (null until it resolves — the legacy per-BL string covers that window).
  readonly suggestedNumber?: string | null;
}

export function buildFinalSettlementDefaults(
  args: BuildFinalSettlementDefaultsArgs,
): DefaultValues<FinalSettlementSubmitValues> {
  const base = baseDefaults({
    side: args.side,
    ticket: args.ticket,
    bl: args.bl,
    allocatedDownpayment: args.allocatedDownpayment,
    advanceOpen: args.advanceOpen,
  });
  // Final-settlement total nets the previously-applied provisional payments too.
  const grossNet = base.total_amount - args.appliedProvisional;
  const settled: Omit<FinalSettlementSubmitValues, 'vat_amount'> = {
    ...base,
    total_amount: Math.max(grossNet, 0),
    applied_provisional_amount: args.appliedProvisional,
  };
  if (args.existing === null) {
    return args.side === 'sell'
      ? applySellFinalCreateDefaults(settled, args.bl, args.suggestedNumber ?? null)
      : settled;
  }
  const withExistingBase = withExisting(settled, args.existing, args.side);
  return {
    ...withExistingBase,
    applied_provisional_amount:
      args.existing.applied_provisional_amount === null
        ? args.appliedProvisional
        : Number(args.existing.applied_provisional_amount),
    reclassify_to_invoice_type: 'Final Settlement',
  };
}

export interface TradePrefill {
  readonly values: Partial<ProvisionalSubmitValues>;
  readonly aiExtractionId: string;
  // The PDF the extraction ran on — seeded into the dialog's Invoice PDF pane
  // so the reviewed document attaches on save without a second upload.
  readonly file?: File;
}

export function applyTradePrefill<T extends DefaultValues<ProvisionalSubmitValues>>(
  defaults: T,
  prefill: TradePrefill | undefined,
): T {
  if (prefill === undefined) return defaults;
  return { ...defaults, ...prefill.values };
}
